// MACHINERY V2 (Stroje) — the katalog DETAIL page's PAGE-LEVEL overlays
// (split out of CatalogDetailV2.tsx at the M2.16c fix pass).
//
// >>> WHY THIS FILE EXISTS. <<< CatalogDetailV2 crossed the project's 300-line
// cap while taking the review's finding-5 sticky-CTA clearance fix, and the
// project rule is that a file at or over the cap is SPLIT before more lines
// land. The seam is the one this very sub-phase already used twice —
// CatalogOverlays.tsx from CatalogBrowseV2.tsx, FleetOverlays.tsx from
// FleetPage.tsx — so the three surfaces are now shaped alike: the page keeps the
// hooks, the handlers and the decisions; this keeps what the page mounts BESIDE
// its content.
//
// >>> MOUNT DEPTH IS THE GUARD, AND IT SURVIVES THIS SPLIT. <<< It returns a
// FRAGMENT, so the popup and the dialog remain siblings of the scroll area
// inside the page's own div — exactly where they were, at exactly the depth they
// were. Same rule CatalogOverlays.tsx:14-21 and FleetPage.tsx:231-245 keep: an
// overlay must not sit inside a clickable ancestor, or a click on its Close /
// backdrop / CTA re-enters that ancestor's onClick.
//
// THE GUARDS MOVED WITH THE ELEMENTS, unchanged: `readinessOpen && model &&
// presentation` and `addOpen && asListItem` are the same two conditions, in the
// same order, so nothing about WHEN either overlay mounts has changed.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { CatalogModelDetail, CatalogModelListItem } from "../../models/models";
import MachineryToastHost from "../feedback/MachineryToastHost";
import ReadinessPopupV2 from "../readiness/ReadinessPopupV2";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import CatalogAddDialogV2 from "./CatalogAddDialogV2";

export interface CatalogDetailOverlaysProps {
  /** The page's id-guarded model. Absent (`undefined`) until the right one is
   *  loaded; `null` while the shared slice slot is empty. */
  model?: CatalogModelDetail | null;
  presentation: MachineryV2ReadinessPresentation | null;
  /** The list-item projection both overlays speak (catalogDetailAdapter.ts). */
  asListItem: CatalogModelListItem | null;
  readinessOpen: boolean;
  onCloseReadiness: () => void;
  addOpen: boolean;
  onCloseAdd: () => void;
  /** Fired ONLY after createMachine resolves — the page navigates away. */
  onCreated: (id?: string) => void;
}

const CatalogDetailOverlays: React.FC<CatalogDetailOverlaysProps> = ({
  model,
  presentation,
  asListItem,
  readinessOpen,
  onCloseReadiness,
  addOpen,
  onCloseAdd,
  onCreated,
}) => (
  <>
    {readinessOpen && model && presentation && (
      <ReadinessPopupV2
        open
        onOpenChange={(o) => {
          if (!o) onCloseReadiness();
        }}
        presentation={presentation}
        machineOrModelName={model.modelName}
        oemPartnerName={model.oemPartnerName}
        manufacturerName={model.manufacturer?.name}
      />
    )}

    {/* A successful add LEAVES the detail page — see `catalogAddedPath` in
        catalogChrome.tsx, and the page's own `onCreated`. Navigating away is
        right there precisely because the user's task on a model detail page has
        just completed, and it gives both catalog entry points ONE predictable
        exit rather than two different silences. */}
    {addOpen && asListItem && (
      <CatalogAddDialogV2
        open
        model={asListItem}
        onClose={onCloseAdd}
        onCreated={onCreated}
      />
    )}

    <MachineryToastHost />
  </>
);

export default CatalogDetailOverlays;
